/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import listSaga from './list';
import byIdSaga from './byId';
import childrenSaga from './children';
import createSaga, { CommentCreateData } from './create';
import updateSaga, { CommentUpdateData } from './update';
import deleteSaga from './delete';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { FileData, PaginatedResponse, Reaction } from '../types';
import { Article } from '../article';
import { User } from '../auth';
import { Business } from '../business';
import { CategoryShort } from '../category';
import { SubCategory } from '../subCategory';

export const moduleName = 'comment';

export type CommentStatus = 'published' | 'deactivated' | 'draft';
export const CommentStatusTitle: Record<CommentStatus, string> = {
  published: 'Published',
  deactivated: 'Hidden',
  draft: 'Draft',
};

export type CommentEntity = 'business' | 'article' | 'international_community' | 'category' | 'subcategory' | 'post';
export const CommentEntityTitle: Record<CommentEntity, string> = {
  business: 'Business',
  article: 'Article',
  international_community: 'International community',
  category: 'Category',
  subcategory: 'Subcategory',
  post: 'Post',
};

export interface Comment {
  id: string;
  text: string;
  status: string;
  entity: CommentEntity;
  files: FileData[],
  user: User;
  business: Business,
  category: CategoryShort,
  subCategory: SubCategory,
  article: Article,
  createdAt: string;
  updatedAt: string;
  numberOfLikes: number;
  numberOfDislikes: number;
  numberOfComments: number;
  numberOfCommentsTotal: number;
  parentComment: string | null;
  reactions: Reaction[];
}

export interface State {
  list: BaseState<PaginatedResponse<Comment>>,
  byId: BaseState<Comment>,
  childrenList: BaseState<PaginatedResponse<Comment, { parentId: string }>>,
  create: BaseState<CommentCreateData>,
  update: BaseState<CommentUpdateData>,
  delete: BaseState<{ id: string }>
}

const models = ['list', 'childrenList', 'byId', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const comment = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = comment;

export function useComment(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* byIdSaga();
  yield* childrenSaga();
  yield* createSaga();
  yield* updateSaga();
  yield* deleteSaga();
}
